/**
 * PATH: apps/ai/src/services/agents/support-agents.ts
 * PURPOSE: DataRetrieverAgent (pre-fetch data) and ResponseValidatorAgent (quality checks)
 */

import { API_BASE } from '@quotech/api';
import type { QueryIntent, ExtractedEntities, Citation } from './agent-types';
import { buildOrganisationHeader } from '../streaming/auth';

// ---------------------------------------------------------------------------
// DataRetrieverAgent — pre-fetch relevant data based on query analysis
// ---------------------------------------------------------------------------

export class DataRetrieverAgent {
  async searchJobs(
    searchTerms: string[], token: string, limit = 50,
  ): Promise<{ jobs: Array<{ id: number; name: string; client: string; status: string }>; total: number }> {
    try {
      const query = searchTerms.join(' ');
      const response = await fetch(
        `${API_BASE}/api/v2/jobs/?search=${encodeURIComponent(query)}&limit=${limit}`,
        { headers: { Authorization: `Bearer ${token}`, ...buildOrganisationHeader() } },
      );
      if (!response.ok) return { jobs: [], total: 0 };
      const data = await response.json();
      return {
        jobs: (data.results || []).map((j: { job_id: number; site_name?: string; client_name?: string; combined_status?: string }) => ({
          id: j.job_id,
          name: j.site_name || `Job #${j.job_id}`,
          client: j.client_name || 'Unknown',
          status: j.combined_status || 'Unknown',
        })),
        total: data.count || 0,
      };
    } catch (e) {
      console.error('[DataRetrieverAgent] searchJobs error:', e);
      return { jobs: [], total: 0 };
    }
  }

  async getJobDetails(jobId: number, token: string): Promise<Record<string, unknown> | null> {
    try {
      const response = await fetch(
        `${API_BASE}/api/v2/jobs/${jobId}/`,
        { headers: { Authorization: `Bearer ${token}`, ...buildOrganisationHeader() } },
      );
      if (!response.ok) return null;
      return await response.json();
    } catch (e) {
      console.error('[DataRetrieverAgent] getJobDetails error:', e);
      return null;
    }
  }
}

// ---------------------------------------------------------------------------
// ResponseValidatorAgent — validate AI response quality
// ---------------------------------------------------------------------------

export class ResponseValidatorAgent {
  validate(
    response: string, intent: QueryIntent, entities: ExtractedEntities,
  ): { isValid: boolean; issues: string[]; suggestedFollowUp?: string } {
    const issues: string[] = [];

    if (intent.type === 'data_lookup' && entities.jobIds?.length) {
      if (!response.match(/\d+/)) issues.push('Response lacks specific data points');
    }
    if (intent.type === 'search') {
      if (!response.includes('found') && !response.includes('no ') && !response.includes('not find')) {
        issues.push('Response does not clearly state search results');
      }
    }
    if (/i don't know|i cannot|unable to/i.test(response) && !/however|alternatively|you could|try/i.test(response)) {
      issues.push('Negative response without alternatives');
    }

    let suggestedFollowUp: string | undefined;
    if (issues.length > 0 && intent.type === 'search') {
      suggestedFollowUp = 'Would you like me to search with different criteria?';
    }

    return { isValid: issues.length === 0, issues, suggestedFollowUp };
  }
}
